/**
 * Almacenamiento persistente y cuota (Req. 1.6, 1.7).
 *
 * En Safari `navigator.storage.persist()` puede devolver false sin que sea una
 * falla: Apple no expone la misma garantia que Chrome. La proteccion real en
 * iPhone es instalar la aplicacion en la pantalla de inicio, mas los respaldos.
 * Aqui se pide la persistencia, se registra el resultado y se muestra en
 * configuracion, sin dar por hecho que la concedieron.
 */

export interface EstadoAlmacenamiento {
  usadoBytes: number | null;
  cuotaBytes: number | null;
  porcentaje: number | null;
  persistente: boolean | null;
}

/**
 * Solicita que el navegador no borre los datos por falta de espacio.
 * Devuelve null si el navegador no ofrece la API.
 */
export async function solicitarPersistencia(): Promise<boolean | null> {
  const almacenamiento = navigator.storage;
  if (almacenamiento?.persist === undefined) return null;

  try {
    if (almacenamiento.persisted !== undefined) {
      const ya = await almacenamiento.persisted();
      if (ya) return true;
    }
    return await almacenamiento.persist();
  } catch {
    return null;
  }
}

export async function consultarPersistencia(): Promise<boolean | null> {
  const almacenamiento = navigator.storage;
  if (almacenamiento?.persisted === undefined) return null;
  try {
    return await almacenamiento.persisted();
  } catch {
    return null;
  }
}

export async function estadoAlmacenamiento(): Promise<EstadoAlmacenamiento> {
  const persistente = await consultarPersistencia();
  const almacenamiento = navigator.storage;

  if (almacenamiento?.estimate === undefined) {
    return { usadoBytes: null, cuotaBytes: null, porcentaje: null, persistente };
  }

  try {
    const { usage, quota } = await almacenamiento.estimate();
    const usadoBytes = usage ?? null;
    const cuotaBytes = quota ?? null;
    const porcentaje =
      usadoBytes !== null && cuotaBytes !== null && cuotaBytes > 0
        ? (usadoBytes / cuotaBytes) * 100
        : null;
    return { usadoBytes, cuotaBytes, porcentaje, persistente };
  } catch {
    return { usadoBytes: null, cuotaBytes: null, porcentaje: null, persistente };
  }
}

export function formatearBytes(bytes: number | null): string {
  if (bytes === null) return 'desconocido';
  if (bytes < 1024) return `${bytes} B`;

  const unidades = ['KB', 'MB', 'GB', 'TB'];
  let valor = bytes / 1024;
  let indice = 0;
  while (valor >= 1024 && indice < unidades.length - 1) {
    valor /= 1024;
    indice++;
  }
  const decimales = valor < 10 ? 1 : 0;
  return `${valor.toFixed(decimales)} ${unidades[indice]}`;
}
